import { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/date';
import { runExport, useExportTemplates, type ExportTemplate } from '@/lib/export';
import { downloadSpreadsheet, type SpreadsheetColumn } from '@/lib/spreadsheet';
import { usePermission, useCanViewCompensations } from '@/lib/permissions';
import { TemplatePicker } from '@/features/export/template-picker';
import { TooltipProvider } from '@/components/ui/tooltip';
import type { EmployeeListItem, EmployeeQueryParams, EmployeesResponse } from '@/types/employee';

const LAST_TEMPLATE_KEY = 'people.export.lastTemplateId';

interface ExportButtonProps {
  queryParams: Omit<EmployeeQueryParams, 'page' | 'limit'>;
  filename?: string;
}

function formatEmploymentType(type: string | null | undefined): string {
  if (!type) return '';
  const labels: Record<string, string> = {
    full_time: 'Full-time',
    part_time: 'Part-time',
    contract: 'Contract',
    intern: 'Intern',
  };
  return labels[type] || type;
}

function formatStatus(status: string): string {
  const labels: Record<string, string> = {
    active: 'Active',
    probation: 'Probation',
    onboarding: 'Onboarding',
    terminated: 'Terminated',
  };
  return labels[status] || status;
}

/**
 * The quick export's columns, in order, with the widths they open at. Kept as
 * one list so a column cannot be added to the sheet and forgotten in the widths
 * — which is how the two exports of this same data drifted apart before
 * (HW-679).
 */
function employeeColumns(includeReview: boolean): SpreadsheetColumn<EmployeeListItem>[] {
  return [
    { header: 'Full Name', width: 25, value: (e) => `${e.firstName} ${e.lastName}` },
    { header: 'Email', width: 30, value: (e) => e.workEmail },
    { header: 'Position', width: 25, value: (e) => e.position?.title ?? '' },
    { header: 'Department', width: 20, value: (e) => e.department?.name ?? '' },
    { header: 'Location', width: 15, value: (e) => e.personalInfo?.address ?? '' },
    { header: 'Seniority Level', width: 15, value: (e) => e.seniorityLevel?.name ?? '' },
    { header: 'Employment Type', width: 15, value: (e) => formatEmploymentType(e.employmentType) },
    { header: 'Status', width: 12, value: (e) => formatStatus(e.status) },
    {
      header: 'Start Date',
      width: 12,
      value: (e) => formatDate(e.startDate, { year: 'numeric', month: '2-digit', day: '2-digit' }),
    },
    // Omitted for non-salary viewers: the backend redacts nextReviewDate, so
    // every row would otherwise falsely read "Not scheduled".
    ...(includeReview
      ? [
          {
            header: 'Next Compensation Review',
            width: 22,
            value: (e: EmployeeListItem) =>
              e.nextReviewDate ? formatDate(e.nextReviewDate, 'monthYear') : 'Not scheduled',
          },
        ]
      : []),
    {
      header: 'Manager',
      width: 25,
      value: (e) => (e.manager ? `${e.manager.firstName} ${e.manager.lastName}` : ''),
    },
  ];
}

async function fetchAllEmployees(
  queryParams: Omit<EmployeeQueryParams, 'page' | 'limit'>
): Promise<EmployeeListItem[]> {
  const allEmployees: EmployeeListItem[] = [];
  const limit = 100; // Max allowed by backend
  let page = 1;
  let hasMore = true;

  // Fetch all pages
  while (hasMore) {
    const response = await api.get<EmployeesResponse>('/employees', {
      params: {
        ...queryParams,
        limit,
        page,
      },
    });

    const { data, meta } = response.data;
    allEmployees.push(...data);

    // Check if there are more pages
    hasMore = page < meta.totalPages;
    page++;
  }

  return allEmployees;
}

export function ExportButton({ queryParams, filename = 'employees' }: ExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [runningTemplateId, setRunningTemplateId] = useState<string | null>(null);
  const [lastTemplateId, setLastTemplateId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(LAST_TEMPLATE_KEY);
    } catch {
      return null;
    }
  });

  // Saved export templates (server-side configurable export). Here the only action is
  // downloading a template, so it's gated by `export.download`; everyone else just sees
  // the quick Excel/CSV export.
  const canUseTemplates = usePermission('export.download');
  const canViewCompensations = useCanViewCompensations();
  const templatesQuery = useExportTemplates({ enabled: canUseTemplates });
  const templates = templatesQuery.data ?? [];
  const busy = isExporting || runningTemplateId !== null;

  const runTemplate = async (t: ExportTemplate) => {
    setRunningTemplateId(t.id);
    try {
      await runExport({ templateId: t.id, format: t.format });
      setLastTemplateId(t.id);
      try {
        localStorage.setItem(LAST_TEMPLATE_KEY, t.id);
      } catch {
        /* localStorage unavailable — last-used just won't persist */
      }
    } catch {
      toast.error('Export failed. Please try again.');
    } finally {
      setRunningTemplateId(null);
    }
  };

  const handleExport = async (format: 'xlsx' | 'csv') => {
    setIsExporting(true);

    try {
      // Fetch all filtered employees
      const employees = await fetchAllEmployees(queryParams);

      if (employees.length === 0) {
        return;
      }

      const timestamp = new Date().toISOString().split('T')[0];
      const exportFilename = `${filename}-${timestamp}`;

      downloadSpreadsheet({
        rows: employees,
        columns: employeeColumns(canViewCompensations),
        filename: exportFilename,
        format,
        sheetName: 'Employees',
      });
    } catch {
      // Export failed - silently fail
    } finally {
      setIsExporting(false);
    }
  };

  const quickExport = (close: () => void) => (
    <>
      <p className="px-2 py-1 text-xs font-medium text-muted-foreground">Quick export</p>
      <button
        type="button"
        onClick={() => {
          void handleExport('xlsx');
          close();
        }}
        className="flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
      >
        <Download className="h-4 w-4" /> Excel (.xlsx)
      </button>
      <button
        type="button"
        onClick={() => {
          void handleExport('csv');
          close();
        }}
        className="flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
      >
        <Download className="h-4 w-4" /> CSV (.csv)
      </button>
    </>
  );

  return (
    // Its own provider, like every other icon in this toolbar: the buttons sit in
    // a StatusCards `actions` slot with no Tooltip ancestor, so each one brings
    // the context its tooltip needs.
    <TooltipProvider delayDuration={0}>
      <TemplatePicker
        contentLabel="Export employees"
        templates={templates}
        lastTemplateId={lastTemplateId}
        onPick={runTemplate}
        tooltip="Export"
        footer={quickExport}
        trigger={
          <Button
            variant="outline"
            size="icon"
            disabled={busy}
            aria-label="Export"
            className="rounded-full cursor-pointer"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          </Button>
        }
      />
    </TooltipProvider>
  );
}
